import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Mail,
  Clock,
  ArrowRight,
  Sparkles,
  ShieldCheck,
  Loader2,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TITLE = "Merci pour votre commande — Audit EM Group";
const DESCRIPTION =
  "Confirmation de paiement de votre audit digital EM Group. Réception sous 3 à 5 jours ouvrés.";

function setMeta(name, content, attr = "name") {
  if (typeof document === "undefined") return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

// ---------------------------------------------------------------------------
// Conversion tracking helpers
// ---------------------------------------------------------------------------
// Remplacez les valeurs ci-dessous par vos vrais identifiants quand vous les
// récupérez depuis Google Ads et Meta Business Manager. Tant que la valeur
// est `null`, l'événement est simplement ignoré (pas d'erreur).
const GOOGLE_ADS_CONVERSION_ID = "AW-18201979242"; // installé site-wide dans index.html
const GOOGLE_ADS_CONVERSION_LABEL = null; // ⚠️ à renseigner : libellé de l'action de conversion "achat audit" (Google Ads → Outils → Conversions)
const META_PIXEL_ID = null; // ex. "1234567890123456"

function fireGoogleAdsConversion(value, currency, transactionId) {
  if (!GOOGLE_ADS_CONVERSION_ID || !GOOGLE_ADS_CONVERSION_LABEL) return;
  if (!window.gtag) {
    // gtag injecté à la volée si pas présent
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_CONVERSION_ID}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
    window.gtag("js", new Date());
    window.gtag("config", GOOGLE_ADS_CONVERSION_ID);
  }
  window.gtag("event", "conversion", {
    send_to: `${GOOGLE_ADS_CONVERSION_ID}/${GOOGLE_ADS_CONVERSION_LABEL}`,
    value,
    currency,
    transaction_id: transactionId,
  });
}

function fireMetaPixelPurchase(value, currency, transactionId) {
  if (!META_PIXEL_ID) return;
  if (!window.fbq) {
    (function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(
      window,
      document,
      "script",
      "https://connect.facebook.net/en_US/fbevents.js",
    );
    window.fbq("init", META_PIXEL_ID);
    window.fbq("track", "PageView");
  }
  window.fbq("track", "Purchase", {
    value,
    currency,
    content_name: "Audit EM Group",
    content_ids: [transactionId],
    content_type: "product",
  });
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------
export default function MerciAudit() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading"); // loading | paid | unknown | error
  const [paymentAmount, setPaymentAmount] = useState(190);
  const [customerEmail, setCustomerEmail] = useState("");

  useEffect(() => {
    document.title = TITLE;
    setMeta("description", DESCRIPTION);
    setMeta("robots", "noindex,follow"); // page de remerciement → pas dans Google
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    if (!sessionId) {
      // accès direct sans paiement → on affiche quand même la page mais sans fire conversion
      setStatus("unknown");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/checkout/status/${sessionId}`,
        );
        if (cancelled) return;
        const isPaid =
          data?.payment_status === "paid" || data?.status === "complete";
        const amount = data?.amount_total
          ? Math.round(data.amount_total / 100)
          : 190;
        setPaymentAmount(amount);
        setCustomerEmail(data?.customer_email || "");
        if (isPaid) {
          setStatus("paid");
          // Fire conversions une seule fois, après confirmation backend
          fireGoogleAdsConversion(amount, "EUR", sessionId);
          fireMetaPixelPurchase(amount, "EUR", sessionId);
        } else {
          setStatus("unknown");
        }
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#050507] text-white relative overflow-hidden">
      {/* Decorative glows */}
      <div
        aria-hidden
        className="absolute -top-32 -left-32 w-[400px] h-[400px] rounded-full bg-[#E52431]/15 blur-3xl"
      />
      <div
        aria-hidden
        className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-[#1F2C6E]/20 blur-3xl"
      />

      <main className="relative max-w-3xl mx-auto px-6 py-20 sm:py-28">
        {/* Top brand bar */}
        <div className="flex items-center gap-3 mb-12">
          <img
            src="/brand/logo-em-group.png"
            alt="EM Group"
            className="w-9 h-9 rounded-md"
          />
          <div>
            <div className="text-sm font-semibold tracking-wide">EM GROUP</div>
            <div className="text-[10px] text-white/50 tracking-[2px]">
              AGENCE DIGITALE PREMIUM
            </div>
          </div>
        </div>

        {/* Success icon + hero */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          data-testid="merci-audit-hero"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] tracking-[2px] text-white/80 mb-6">
            <Sparkles className="w-3.5 h-3.5 text-[#E52431]" />
            COMMANDE CONFIRMÉE
          </div>

          <div className="flex items-start gap-4 mb-6">
            <div className="shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-[#E52431] to-[#1F2C6E] flex items-center justify-center shadow-lg shadow-[#E52431]/30">
              <CheckCircle2 className="w-7 h-7 text-white" strokeWidth={2.2} />
            </div>
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold leading-tight tracking-tight">
                Merci pour votre commande.
              </h1>
              <p className="text-white/60 text-base sm:text-lg mt-3 max-w-xl leading-relaxed">
                Votre paiement a bien été reçu. Notre équipe a déjà démarré
                l'analyse de votre univers digital — vous serez entre de bonnes
                mains.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Status banner */}
        {status === "loading" && (
          <div
            className="mt-8 flex items-center gap-3 text-sm text-white/60"
            data-testid="merci-audit-status-loading"
          >
            <Loader2 className="w-4 h-4 animate-spin" />
            Vérification du paiement en cours…
          </div>
        )}
        {status === "paid" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mt-8 p-5 rounded-2xl border border-emerald-500/30 bg-emerald-500/5"
            data-testid="merci-audit-status-paid"
          >
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-5 h-5 text-emerald-400" />
              <div className="text-sm">
                <div className="font-semibold text-white">
                  Paiement confirmé · {paymentAmount} € TTC
                </div>
                <div className="text-white/60 text-xs mt-0.5">
                  Reçu envoyé{customerEmail ? ` à ${customerEmail}` : ""} par
                  Stripe.
                </div>
              </div>
            </div>
          </motion.div>
        )}
        {status === "error" && (
          <div className="mt-8 p-5 rounded-2xl border border-amber-500/30 bg-amber-500/5 text-sm text-amber-200">
            Nous n'avons pas pu vérifier le paiement automatiquement. Si vous
            avez bien été débité, votre commande est tout de même prise en
            compte — nous reviendrons vers vous très vite.
          </div>
        )}

        {/* Next steps */}
        <section className="mt-14" data-testid="merci-audit-next-steps">
          <div className="text-[11px] tracking-[2px] text-white/40 font-semibold mb-6">
            — PROCHAINES ÉTAPES
          </div>
          <ol className="space-y-5">
            <li className="flex gap-5">
              <div className="shrink-0 w-9 h-9 rounded-full border border-[#E52431]/40 bg-[#E52431]/10 flex items-center justify-center text-[#E52431] font-bold text-sm">
                1
              </div>
              <div>
                <h3 className="font-semibold text-white">
                  Prise de contact sous 24 h
                </h3>
                <p className="text-white/55 text-sm mt-1 leading-relaxed">
                  Vous recevrez un email de cadrage avec un court questionnaire
                  pour finaliser le périmètre de votre audit.
                </p>
              </div>
            </li>
            <li className="flex gap-5">
              <div className="shrink-0 w-9 h-9 rounded-full border border-white/15 bg-white/5 flex items-center justify-center text-white/80 font-bold text-sm">
                2
              </div>
              <div>
                <h3 className="font-semibold text-white">
                  Analyse 360° par nos experts
                </h3>
                <p className="text-white/55 text-sm mt-1 leading-relaxed">
                  Image de marque, site web, SEO, visibilité, conversion : nous
                  passons votre présence au peigne fin.
                </p>
              </div>
            </li>
            <li className="flex gap-5">
              <div className="shrink-0 w-9 h-9 rounded-full border border-white/15 bg-white/5 flex items-center justify-center text-white/80 font-bold text-sm">
                3
              </div>
              <div>
                <h3 className="font-semibold text-white">
                  Livraison de votre rapport
                </h3>
                <p className="text-white/55 text-sm mt-1 leading-relaxed">
                  Rapport PDF détaillé + plan d'action priorisé, livré sous 3 à
                  5 jours ouvrés.
                </p>
              </div>
            </li>
          </ol>
        </section>

        {/* ETA card */}
        <div
          className="mt-10 p-6 rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-transparent flex items-start gap-4"
          data-testid="merci-audit-eta"
        >
          <div className="shrink-0 w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
            <Clock className="w-5 h-5 text-[#E52431]" />
          </div>
          <div className="flex-1">
            <div className="text-[11px] tracking-[2px] text-white/40 font-semibold">
              DÉLAI DE LIVRAISON
            </div>
            <div className="text-lg font-semibold text-white mt-1">
              3 à 5 jours ouvrés
            </div>
            <p className="text-white/55 text-sm mt-1">
              Une question entre-temps ?{" "}
              <a
                href="mailto:contact@emgroupdigital.fr"
                className="underline decoration-white/30 hover:decoration-[#E52431] hover:text-white transition"
              >
                <Mail className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
                contact@emgroupdigital.fr
              </a>
            </p>
          </div>
        </div>

        {/* Reassurance / discrete secondary CTAs */}
        <section className="mt-16" data-testid="merci-audit-secondary-cta">
          <div className="text-[11px] tracking-[2px] text-white/40 font-semibold mb-5">
            — EN ATTENDANT
          </div>
          <p className="text-white/65 text-sm max-w-xl leading-relaxed">
            Pendant que nous préparons votre audit, vous pouvez explorer nos
            autres prestations. Aucune obligation, juste de l'inspiration.
          </p>

          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            <Link
              to="/#branding"
              className="group p-5 rounded-xl border border-white/10 hover:border-[#E52431]/40 bg-white/[0.02] hover:bg-white/[0.04] transition flex items-center justify-between"
              data-testid="merci-audit-cta-branding"
            >
              <div>
                <div className="text-[10px] tracking-[2px] text-white/40 font-semibold">
                  BRANDING
                </div>
                <div className="font-semibold mt-1">
                  Logo & identité visuelle
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-[#E52431] group-hover:translate-x-1 transition" />
            </Link>
            <Link
              to="/#web"
              className="group p-5 rounded-xl border border-white/10 hover:border-[#1F2C6E]/60 bg-white/[0.02] hover:bg-white/[0.04] transition flex items-center justify-between"
              data-testid="merci-audit-cta-web"
            >
              <div>
                <div className="text-[10px] tracking-[2px] text-white/40 font-semibold">
                  SITE WEB
                </div>
                <div className="font-semibold mt-1">
                  Création & refonte premium
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-[#3D52C7] group-hover:translate-x-1 transition" />
            </Link>
            <Link
              to="/#tarifs"
              className="group p-5 rounded-xl border border-white/10 hover:border-[#E52431]/40 bg-white/[0.02] hover:bg-white/[0.04] transition flex items-center justify-between"
              data-testid="merci-audit-cta-visuels"
            >
              <div>
                <div className="text-[10px] tracking-[2px] text-white/40 font-semibold">
                  VISUELS
                </div>
                <div className="font-semibold mt-1">
                  Abonnement réseaux sociaux
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-[#E52431] group-hover:translate-x-1 transition" />
            </Link>
            <Link
              to="/"
              className="group p-5 rounded-xl border border-white/10 hover:border-white/20 bg-white/[0.02] hover:bg-white/[0.04] transition flex items-center justify-between"
              data-testid="merci-audit-cta-home"
            >
              <div>
                <div className="text-[10px] tracking-[2px] text-white/40 font-semibold">
                  RETOUR
                </div>
                <div className="font-semibold mt-1">Accueil EM Group</div>
              </div>
              <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-white group-hover:translate-x-1 transition" />
            </Link>
          </div>
        </section>

        {/* Footer signature */}
        <div className="mt-20 pt-8 border-t border-white/5 text-center text-xs text-white/40">
          EM Group · Agence digitale premium · contact@emgroupdigital.fr ·
          emgroupdigital.fr
        </div>
      </main>
    </div>
  );
}
